import React, { useState, useEffect } from 'react';
import { X, Upload } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { storage, extensionsCollection, db } from '../lib/firebase';
import toast from 'react-hot-toast';
import type { Extension } from '../types/index';

interface ExtensionFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedExtension: Extension) => void;
  extension: Extension | null;
  categories: { id: string; name: string }[];
}

export default function ExtensionFormModal({ isOpen, onClose, onSuccess, extension, categories }: ExtensionFormModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    version: '',
    author: '',
    categoryId: '',
    file: null as File | null,
    thumbnail: null as File | null
  });

  useEffect(() => {
    if (extension) {
      setFormData({
        name: extension.name,
        description: extension.description,
        version: extension.version,
        author: extension.author,
        categoryId: extension.categoryId,
        file: null,
        thumbnail: null
      });
    }
  }, [extension]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let updatedExtension: Extension;

      if (extension) {
        // Update existing extension
        const extensionRef = doc(db, 'extensions', extension.id);
        const updateData: Partial<Extension> = {
          updatedAt: new Date()
        };

        // Only include changed fields
        if (formData.name !== extension.name) updateData.name = formData.name;
        if (formData.description !== extension.description) updateData.description = formData.description;
        if (formData.version !== extension.version) updateData.version = formData.version;
        if (formData.author !== extension.author) updateData.author = formData.author;
        if (formData.categoryId !== extension.categoryId) {
          updateData.categoryId = formData.categoryId;
          updateData.category = categories.find(cat => cat.id === formData.categoryId)?.name || 'Uncategorized';
        }

        // Only update files if new ones are selected
        if (formData.thumbnail) {
          const thumbnailName = `${formData.name.toLowerCase().replace(/\s+/g, '-')}-v${formData.version}-thumbnail`;
          const thumbnailPath = `thumbnails/${thumbnailName}`;
          const thumbnailRef = ref(storage, thumbnailPath);
          await uploadBytes(thumbnailRef, formData.thumbnail);
          updateData.thumbnailUrl = await getDownloadURL(thumbnailRef);
        }

        if (formData.file) {
          const fileName = `${formData.name.toLowerCase().replace(/\s+/g, '-')}-v${formData.version}.js`;
          const filePath = `extensions/${fileName}`;
          const fileRef = ref(storage, filePath);
          const metadata = {
            contentType: 'application/javascript',
            contentDisposition: `attachment; filename="${fileName}"`
          };
          await uploadBytes(fileRef, formData.file, { customMetadata: metadata });
          updateData.downloadUrl = `gs://extension-store-a9909.firebasestorage.app/${filePath}`;
        }

        // Only update if there are changes
        if (Object.keys(updateData).length > 1) { // > 1 because updatedAt is always included
          await updateDoc(extensionRef, updateData);
          updatedExtension = { ...extension, ...updateData };
          toast.success('Extension updated successfully');
        } else {
          toast.success('No changes to update');
          onClose();
          return;
        }
      } else {
        // Create new extension
        if (!formData.file || !formData.thumbnail) {
          toast.error('Please select both extension file and thumbnail');
          return;
        }

        // Upload files and create new extension
        const thumbnailName = `${formData.name.toLowerCase().replace(/\s+/g, '-')}-v${formData.version}-thumbnail`;
        const thumbnailPath = `thumbnails/${thumbnailName}`;
        const thumbnailRef = ref(storage, thumbnailPath);
        await uploadBytes(thumbnailRef, formData.thumbnail);
        const thumbnailUrl = await getDownloadURL(thumbnailRef);

        const fileName = `${formData.name.toLowerCase().replace(/\s+/g, '-')}-v${formData.version}.js`;
        const filePath = `extensions/${fileName}`;
        const fileRef = ref(storage, filePath);
        const metadata = {
          contentType: 'application/javascript',
          contentDisposition: `attachment; filename="${fileName}"`
        };
        await uploadBytes(fileRef, formData.file, { customMetadata: metadata });
        const downloadUrl = `gs://extension-store-a9909.firebasestorage.app/${filePath}`;

        const newExtensionData = {
          name: formData.name,
          description: formData.description,
          version: formData.version,
          author: formData.author,
          categoryId: formData.categoryId,
          category: categories.find(cat => cat.id === formData.categoryId)?.name || 'Uncategorized',
          downloadUrl,
          thumbnailUrl,
          downloads: 0,
          totalRatings: 0,
          totalRatingSum: 0,
          averageRating: 0,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        const docData = {
          ...newExtensionData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        const docRef = await addDoc(extensionsCollection, docData);
        updatedExtension = { id: docRef.id, ...newExtensionData };
        toast.success('Extension uploaded successfully');
      }

      onSuccess(updatedExtension);
      onClose();
    } catch (error) {
      console.error('Error handling extension:', error);
      toast.error(`Failed to ${extension ? 'update' : 'upload'} extension`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/80 dark:bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full p-8 shadow-2xl transform transition-all animate-slideIn">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent animate-gradient">
            {extension ? 'Edit Extension' : 'Upload New Extension'}
          </h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="group">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 transition-colors">
              Name
            </label>
            <input
              type="text"
              required={!extension}
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition-all transform hover:scale-[1.01] dark:focus:border-purple-500 dark:focus:ring-purple-500"
              placeholder="Extension name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              required={!extension}
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Version</label>
              <input
                type="text"
                required={!extension}
                value={formData.version}
                onChange={e => setFormData(prev => ({ ...prev, version: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Author</label>
              <input
                type="text"
                required={!extension}
                value={formData.author}
                onChange={e => setFormData(prev => ({ ...prev, author: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Category</label>
            <select
              value={formData.categoryId}
              onChange={e => setFormData(prev => ({ ...prev, categoryId: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required={!extension}
            >
              <option value="">Select a category</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Extension File {extension && '(Optional)'}</label>
            <input
              type="file"
              required={!extension}
              onChange={e => setFormData(prev => ({ ...prev, file: e.target.files?.[0] || null }))}
              className="mt-1 block w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Thumbnail Image {extension && '(Optional)'}</label>
            <input
              type="file"
              required={!extension}
              accept="image/*"
              onChange={e => setFormData(prev => ({ ...prev, thumbnail: e.target.files?.[0] || null }))}
              className="mt-1 block w-full"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center items-center space-x-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 transform transition-all hover:scale-[1.02] active:scale-[0.98] shadow-md"
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                <span>Processing...</span>
              </div>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                <span>Upload Extension</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
} 